import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { GALLERY_CATEGORIES } from "@/data/content";
import { useContent } from "@/context/ContentContext";

function spanClass(span) {
  if (span === "wide") return "md:col-span-2";
  if (span === "tall") return "md:row-span-2";
  return "";
}

export default function Gallery() {
  const { content: c } = useContent();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/gallery", { params: cat === "All" ? {} : { category: cat } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [cat]);

  return (
    <div data-testid="gallery-page" className="pt-28 md:pt-32">
      <section className="container-lux py-16 md:py-20">
        <Reveal>
          <p className="overline mb-6">Our Work</p>
          <h1 className="font-serif-lux text-5xl md:text-7xl font-light leading-[1.02] mb-8">
            {c.gallery_h1}
          </h1>
          <p className="text-[#4A4A4A] text-lg max-w-2xl leading-relaxed">{c.gallery_intro}</p>
        </Reveal>
      </section>

      <section className="container-lux">
        <div className="flex flex-wrap gap-2 md:gap-3 mb-10 border-y border-[#1A1A1A]/10 py-5">
          {GALLERY_CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setCat(c)}
              className={`text-xs uppercase tracking-[0.14em] px-4 py-2 transition-colors ${
                cat === c ? "text-[#0A0A0A] border-b border-[#D4AF37]" : "text-[#8a8a80] hover:text-[#1A1A1A]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-32 text-center text-[#8a8a80] tracking-[0.2em] uppercase text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-32 text-center text-[#8a8a80]">No media in this category yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-[220px] md:auto-rows-[280px] gap-4 md:gap-5 pb-24">
            {items.map((it, i) => (
              <motion.div
                key={it.id}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: (i % 6) * 0.06 }}
                className={`media-zoom relative cursor-pointer group ${spanClass(it.span)}`}
                onClick={() => setActive(it)}
                data-testid={`gallery-item-${it.id}`}
              >
                <img src={mediaUrl(it.url)} alt={it.title} loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
                {it.media_type === "video" && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="w-14 h-14 rounded-full bg-[#D4AF37]/90 flex items-center justify-center">
                      <Play className="text-black ml-1" size={22} />
                    </span>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 p-5">
                  <span className="text-[#D4AF37] text-[0.65rem] uppercase tracking-[0.2em]">{it.category}</span>
                  <h3 className="font-serif-lux text-white text-xl md:text-2xl font-light">{it.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-[#0A0A0A] text-white py-24 md:py-28 text-center">
        <Reveal>
          <h2 className="font-serif-lux text-3xl md:text-5xl font-light mb-8">{c.gallery_cta_heading}</h2>
          <Link to="/quote" data-testid="gallery-cta-btn" className="btn-gold">Begin Your Journey</Link>
        </Reveal>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)} data-testid="gallery-lightbox"
          >
            <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={() => setActive(null)} aria-label="Close">
              <X size={30} />
            </button>
            <motion.div
              initial={{ scale: 0.94 }} animate={{ scale: 1 }} exit={{ scale: 0.94 }}
              className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}
            >
              {active.media_type === "video" ? (
                <video src={mediaUrl(active.url)} controls autoPlay className="w-full max-h-[80vh]" />
              ) : (
                <img src={mediaUrl(active.url)} alt={active.title} className="w-full max-h-[80vh] object-contain" />
              )}
              <div className="mt-4 text-center">
                <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em]">{active.category}</span>
                <h3 className="font-serif-lux text-white text-2xl font-light">{active.title}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
